import { useContext, useEffect, useState } from "react"
import { requestTicketsChronologicalCount, requestTicketsFirstReplyTimeChronological, requestTicketsStatsOverview, requestUserStatsOverview } from "../requests/stats_requests"
import { LuTicket, LuTicketCheck, LuTicketX } from "react-icons/lu"

import { requestCategoryTicketNumbers } from "../requests/setup_requests";
import { ContentContext } from "../contexts/ContentContext";
import { CircularGraph, LineChart, LineChartTwoLines } from "./ChartComponents";
import { AccountContext } from "../contexts/AccountContexts";
import { CategoryBubble } from "./TicketComponents";
import { useTranslation } from "react-i18next";
import { pageLoadingActive } from "./Background";


export const TicketStatsOverview = () => {

    const { t } = useTranslation('dashboard')
    const { openErrorModal } = useContext(ContentContext)

    const [ticketStats, setTicketStats] = useState({})
    const [failed, setFailed] = useState(false)
    const [loading, setLoading] = useState(false)

    useEffect(() => {
        setLoading(true)
        requestTicketsStatsOverview({})
            .then((data) => {
                setTicketStats(data.ticket_stats)
                setLoading(false)
            })
            .catch((error) => {
                setLoading(false)
                console.error(error.status, error.message)
                openErrorModal(error)
            })
    }, [])


    return (
        <div className="flex flex-col max-w-[1200px] px-5 py-3 gap-5 min-h-48">

            <div className="flex w-full justify-start gap-5">
                <div className="flex w-full justify-start items-center gap-5">
                    <TicketStatBox title={t("Open tickets")} count={ticketStats.n_open} icon={<LuTicket />} style={"bg-blue-400 text-white"} />
                    <TicketStatBox title={t("Resolved tickets")} count={ticketStats.n_resolved} icon={<LuTicketCheck />} style={"bg-green-400 text-white"} />
                    <TicketStatBox title={t("Closed tickets")} count={ticketStats.n_closed} icon={<LuTicketX />} style={"bg-red-600 text-white"} />
                    <TicketStatBox title={t("Total tickets")} count={ticketStats.n_total} icon={<LuTicket />} style={"bg-amber-500 text-white"} />
                </div>
                <CategoryStatsDoughnut />
            </div>



            {
                loading &&
                <div className="flex justify-center my-auto">
                    <span className="loading loading-spinner"></span>
                </div>
            }

            {
                failed &&
                <div className="text-red-600 w-full flex justify-center">
                    <span>{t('Could not retrieve stats')}</span>
                </div>
            }
        </div>
    )
}

const TicketStatBox = ({ count, title, icon, style }) => {


    return (
        <div className="flex flex-col rounded-lg border border-primary bg-white dark:bg-gray-700 py-8 px-2 items-center gap-2 min-w-36">
            <div className={`rounded-xl  p-2 ${style}`}>
                <span className="text-2xl">{icon}</span>
            </div>
            <span className="font-semibold">{title}</span>
            <span className="text-2xl font-semibold">{count}</span>
        </div>
    )
}


export const CategoryStatsDoughnut = () => {
    const { t } = useTranslation('common')
    const { categories, openErrorModal } = useContext(ContentContext)
    const [categoryStats, setCategoryStats] = useState([])


    useEffect(() => {
        requestCategoryTicketNumbers({})
            .then((data) => {
                let catStats = []
                Object.keys(data.ticket_numbers).filter((a) => categories[a] || true).sort((a, b) => categories[a].value - categories[b].value).forEach((element) => {

                    catStats.push(
                        {
                            label: categories[element].title,
                            count: data.ticket_numbers[element]
                        }
                    )
                })
                setCategoryStats(catStats)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
            })
    }, [])

    const categoryColors = {
        [t("Ban removal request", { 'ns': 'common' })]: "#000000",
        [t("Violating the rules of the chat", { 'ns': 'common' })]: "#431407",
        [t("Reporting suspected cheaters or bots", { 'ns': 'common' })]: "#082f49",
        [t("Report bugs", { 'ns': 'common' })]: "#dc2626",
        [t("Payment problems (fCoin)", { 'ns': 'common' })]: "#2dd4bf",
        [t("Suggestion and complaint", { 'ns': 'common' })]: "#6b21a8",
        [t("Advertising and collaboration", { 'ns': 'common' })]: "#9a3412",
        [t("Registration problems", { 'ns': 'common' })]: "#1d4ed8",
        [t("Other", { 'ns': 'common' })]: "#1f2937",
        [t("Category11", { 'ns': 'common' })]: "#dc2626",
        [t("Support System Issues", { 'ns': 'common' })]: "#6b21a8",
    }

    return (
        <div className="w-[500px] h-[250px] m-auto">
            <CircularGraph data={categoryStats} labelColors={categoryColors} />
        </div>
    )
}


export const TicketCountGraph = () => {
    const [datasets, setDatasets] = useState({})
    const { openErrorModal } = useContext(ContentContext)

    useEffect(() => {
        requestTicketsChronologicalCount({})
            .then((data) => {
                setDatasets(data)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
            })
    }, [])

    if (!datasets.hours_to_count) {
        return <></>
    }

    return (
        <div className="w-full">
            <LineChart datasets={datasets} />
        </div>
    );
};


export const TicketFirstReplyGraph = () => {
    const [datasets, setDatasets] = useState({})
    const { openErrorModal } = useContext(ContentContext)

    useEffect(() => {
        requestTicketsFirstReplyTimeChronological({})
            .then((data) => {
                setDatasets(data)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
            })
    }, [])

    if (!datasets.hours_to_count) {
        return <></>
    }

    return (
        <div className="w-full">
            <LineChartTwoLines datasets={datasets} />
        </div>
    );
};


export const UserStatsOverview = () => {
    const { t } = useTranslation(['dashboard', 'common'])

    const { user, language } = useContext(AccountContext)
    const { categories, deletedCategories, openErrorModal } = useContext(ContentContext)
    const [userStats, setUserStats] = useState({})

    const [openTicketCategories, setOpenTicketCategories] = useState({})
    const [openTicketStatuses, setOpenTicketStatuses] = useState({})

    const labels = {
        n_open: "Open Tickets",
        n_resolved: "Resolved Tickets",
        n_closed: "Closed Tickets"
    }


    useEffect(() => {
        pageLoadingActive(true)
        requestUserStatsOverview({})
            .then((data) => {
                let uStats = []
                Object.keys(data.user_stats).forEach((element) => {

                    uStats.push(
                        {
                            label: labels[element],
                            count: data.user_stats[element]
                        }
                    )
                })
                setUserStats(uStats)

                let catStats = []
                Object.keys(data.open_ticket_categories).forEach((element) => {
                    catStats.push(
                        {
                            label: categories[element] ? (categories[element]?.locales?.[language] ? categories[element]?.locales[language] : categories[element].title) : deletedCategories[element] ? deletedCategories[element].title : "UNK",
                            count: data.open_ticket_categories[element]
                        }
                    )
                })
                setOpenTicketCategories(catStats)

                let statusStats = []
                Object.keys(data.open_ticket_statuses).forEach((element) => {
                    statusStats.push(
                        {
                            label: element,
                            count: data.open_ticket_statuses[element]
                        }
                    )
                })
                setOpenTicketStatuses(statusStats)
                pageLoadingActive(false)
            })
            .catch((error) => {
                console.error(error.status, error.message)
                openErrorModal(error)
                pageLoadingActive(false)
            })
    }, [])

    if (Object.keys(userStats).length === 0) {
        return <></>
    }


    const categoryColors = {
        [t("Ban removal request", { 'ns': 'common' })]: "#000000",
        [t("Violating the rules of the chat", { 'ns': 'common' })]: "#431407",
        [t("Reporting suspected cheaters or bots", { 'ns': 'common' })]: "#082f49",
        [t("Report bugs", { 'ns': 'common' })]: "#dc2626",
        [t("Payment problems (fCoin)", { 'ns': 'common' })]: "#2dd4bf",
        [t("Suggestion and complaint", { 'ns': 'common' })]: "#6b21a8",
        [t("Advertising and collaboration", { 'ns': 'common' })]: "#9a3412",
        [t("Registration problems", { 'ns': 'common' })]: "#1d4ed8",
        [t("Other", { 'ns': 'common' })]: "#1f2937",
        [t("Category11", { 'ns': 'common' })]: "#dc2626",
        [t("Support System Issues", { 'ns': 'common' })]: "#6b21a8",
    }


    const ticketColors = {
        [t("Open Tickets", { 'ns': 'common' })]: "#38bdf8",
        [t("Resolved Tickets", { 'ns': 'common' })]: "#4ade80",
        [t("Closed Tickets", { 'ns': 'common' })]: "#dc2626"
    }


    const ticketStatusColors = {
        [t("NEW TICKET", { 'ns': 'common' })]: "#facc15",
        [t("ACTIVE", { 'ns': 'common' })]: "#60a5fa",
        [t("ON HOLD", { 'ns': 'common' })]: "#8b5cf6",
        [t("REPLIED", { 'ns': 'common' })]: "#60a5fa",
        [t("WAITING DEV", { 'ns': 'common' })]: "#f59e0b",
        [t("RESOLVED", { 'ns': 'common' })]: "#4ade80",
        [t("CLOSED", { 'ns': 'common' })]: "#dc2626"
    }

    const hasHadTickets = userStats.map((stat) => stat.count).reduce((a, b) => a + b)


    return (
        <div className="flex gap-5">
            <div className="flex flex-col gap-1">
                <span className="font-semibold">{t('My categories')}</span>
                {
                    user.categories.length !== 0 ?
                        user.categories.map((category) =>
                            <span key={category}><CategoryBubble ticketCategory={category} /></span>
                        )
                        :
                        <span>{t('No assigned categories')}</span>
                }
            </div>
            {
                hasHadTickets !== 0 &&
                <>
                    <div className="flex flex-col gap-1 h-[300px] w-[300px]">
                        <span className="font-semibold">{t('My tickets')}</span>
                        <CircularGraph data={userStats} labelColors={ticketColors} />
                    </div>
                    <div className="flex flex-col gap-1 h-[300px] w-[300px]">
                        <span className="font-semibold">{t("My tickets' categories")}</span>
                        <CircularGraph data={openTicketCategories} labelColors={categoryColors} />
                    </div>
                    <div className="flex flex-col gap-1 h-[300px] w-[300px] ">
                        <span className="font-semibold">{t("My tickets' statuses")}</span>
                        <CircularGraph data={openTicketStatuses} labelColors={ticketStatusColors} />
                    </div>
                </>
            }
        </div>
    )
}